import ProviderAPIClient from '@/features/auth/ProviderAPIClient'
import { OrderingSessionGoneError } from '@/features/ordering/errors'
import { readSessionImdbOrderId } from '@/utils/orderDraftPatientId'
import * as Sentry from '@sentry/react'
import { useEffect, useState } from 'react'

export type OrderingSessionState =
  | { status: 'idle' }
  | { status: 'loading' }
  /** Still an open draft — safe to edit and submit. */
  | { status: 'open' }
  /** Already produced an order; `orderId` is the order it created. */
  | { status: 'submitted'; orderId: string }
  /** Row absent or cross-org (404). */
  | { status: 'gone' }
  | { status: 'error'; message: string }

type CachedSession = {
  sessionId: string
  state: OrderingSessionState
}

/**
 * Resolves whether an ordering session is still editable, for route guards that must
 * decide before the order pages mount.
 *
 * Mirrors `useOrderResourceScope`: results are keyed by `sessionId`, so a changed id
 * reports `loading` synchronously instead of leaking the previous verdict for a frame
 * and briefly admitting the wrong session.
 *
 * Deliberately has no optimistic default. Reporting `open` while the fetch is in flight
 * would mount the editable form for one render, which is enough for autosave to arm and
 * write to a session that must not be touched.
 */
export function useOrderingSessionState(sessionId: string | undefined): OrderingSessionState {
  const id = sessionId?.trim() ?? ''

  const [cached, setCached] = useState<CachedSession>(() =>
    id ? { sessionId: id, state: { status: 'loading' } } : { sessionId: '', state: { status: 'idle' } }
  )

  useEffect(() => {
    if (!id) {
      setCached({ sessionId: '', state: { status: 'idle' } })
      return
    }

    let cancelled = false
    const controller = new AbortController()
    setCached({ sessionId: id, state: { status: 'loading' } })

    void (async () => {
      try {
        const session = await ProviderAPIClient.getInstance().getOrderingSession(id, controller.signal)
        if (cancelled) return
        const orderId = readSessionImdbOrderId(session)
        setCached({
          sessionId: id,
          state: orderId ? { status: 'submitted', orderId } : { status: 'open' },
        })
      } catch (err) {
        if (cancelled) return
        if (err instanceof OrderingSessionGoneError) {
          setCached({ sessionId: id, state: { status: 'gone' } })
          return
        }
        if (err instanceof DOMException && err.name === 'AbortError') return
        const message = err instanceof Error ? err.message : 'Unable to verify ordering session'
        setCached({ sessionId: id, state: { status: 'error', message } })
        Sentry.captureException(err, { tags: { operation: 'ordering.session_state' } })
      }
    })()

    return () => {
      cancelled = true
      controller.abort()
    }
  }, [id])

  if (!id) {
    return { status: 'idle' }
  }

  if (cached.sessionId !== id) {
    return { status: 'loading' }
  }

  return cached.state
}
